/**
 * Packages API Client
 * Package System — product/service bundles for rental, sales, service domains
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type PackageType = 'RENTAL' | 'SALES' | 'SERVICE' | 'MIXED' | 'PROMOTIONAL' | 'FRANCHISE';
/** Derived status from isActive boolean — used in UI only */
export type PackageStatus = 'ACTIVE' | 'INACTIVE';
export type PackageLinkingMode = 'CATEGORY' | 'EQUIPMENT';

export interface LinkedProductRef {
  productId: string;
  sortOrder: number;
  product: {
    id: string;
    sku: string;
    name: string;
  };
}
export type PricingModel = 'FIXED' | 'DISCOUNTED' | 'TIERED';
export type PackageScope = 'CENTRAL' | 'LOCAL';
export type PackageItemType =
  | 'PRODUCT'
  | 'SERVICE'
  | 'RENTAL_EQUIPMENT'
  | 'ACCESSORY'
  | 'SERVICE_ITEM';
export type PackageItemSettlement = 'INCLUDED' | 'RENTAL' | 'CONSIGNMENT';
/** Csomag-tétel besorolás (ADR-118 Am.2 D-1 / STORY-1205). */
export type PackageItemCategory = 'FREE' | 'RENTABLE' | 'CONSIGNMENT';

export interface PackageItem {
  id: string;
  packageId: string;
  itemType: PackageItemType;
  settlement: PackageItemSettlement;
  sortOrder: number;
  productId: string | null;
  rentalEquipmentId: string | null;
  quantity: number;
  isOptional: boolean;
  isSubstitutable: boolean;
  isReturnable: boolean;
  unitPrice: number | null;
  notes: string | null;
  // STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1)
  category: PackageItemCategory;
  defaultQuantity: number;
  isQuantityEditable: boolean;
  visibleOnWebshop: boolean;
}

export interface Package {
  id: string;
  tenantId: string;
  packageCode: string;
  name: string;
  description: string | null;
  type: PackageType;
  scope: PackageScope;
  pricingModel: PricingModel;
  fixedPrice: number | null;
  discountPercent: number | null;
  dailyRate: number | null;
  weeklyRate: number | null;
  monthlyRate: number | null;
  depositAmount: number | null;
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  linkedEquipmentCategories: string[];
  imageUrl: string | null;
  thumbnailUrl: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  deletedAt: string | null;
  items: PackageItem[];
  linkingMode: PackageLinkingMode;
  linkedProducts: LinkedProductRef[];
}

export interface PackageListResponse {
  data: Package[];
  meta: {
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

/**
 * Response for GET /packages/type/:type — no pagination meta
 */
export interface PackagesByTypeResponse {
  data: Package[];
}

/**
 * Response for GET /packages/by-equipment-category/:category
 */
export interface PackagesByEquipmentCategoryResponse {
  data: Package[];
}

export interface PackageResponse {
  data: Package;
}

export interface PackageFilters {
  type?: PackageType;
  isActive?: boolean;
  search?: string;
  scope?: PackageScope;
  pricingModel?: PricingModel;
  page?: number;
  limit?: number;
}

export interface CreatePackageDTO {
  packageCode: string;
  name: string;
  description?: string;
  type: PackageType;
  scope?: PackageScope;
  pricingModel: PricingModel;
  fixedPrice?: number;
  discountPercent?: number;
  dailyRate?: number;
  weeklyRate?: number;
  monthlyRate?: number;
  depositAmount?: number;
  validFrom?: string;
  validTo?: string;
  isActive?: boolean;
  linkedEquipmentCategories?: string[] | undefined;
  linkingMode?: PackageLinkingMode | undefined;
  linkedProductIds?: string[] | undefined;
}

export interface UpdatePackageDTO {
  packageCode?: string;
  name?: string;
  description?: string;
  type?: PackageType;
  scope?: PackageScope;
  pricingModel?: PricingModel;
  fixedPrice?: number;
  discountPercent?: number;
  dailyRate?: number;
  weeklyRate?: number;
  monthlyRate?: number;
  depositAmount?: number;
  validFrom?: string;
  validTo?: string;
  isActive?: boolean;
  linkedEquipmentCategories?: string[] | undefined;
  linkingMode?: PackageLinkingMode | undefined;
  linkedProductIds?: string[] | undefined;
}

export interface AddPackageItemDTO {
  itemType: PackageItemType;
  settlement?: PackageItemSettlement;
  productId?: string;
  rentalEquipmentId?: string;
  quantity?: number;
  isOptional?: boolean;
  isSubstitutable?: boolean;
  isReturnable?: boolean;
  unitPrice?: number;
  notes?: string;
  sortOrder?: number;
  // STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1). visibleOnWebshop-ot
  // CONSIGNMENT-nél a backend DB-trigger FALSE-ra forceolja (ADR-118 Am.2 D-4).
  category?: PackageItemCategory;
  defaultQuantity?: number;
  isQuantityEditable?: boolean;
  visibleOnWebshop?: boolean;
}

/**
 * P1-fix — meglévő csomag-tétel attribútum-PATCH (BE: PATCH /packages/:id/items/:itemId).
 * itemType/FK immutable (use remove+add); csak a szerkeszthető mezők, mind opcionális (diff-patch).
 */
export interface UpdatePackageItemDTO {
  quantity?: number;
  unitPrice?: number;
  sortOrder?: number;
  category?: PackageItemCategory;
  defaultQuantity?: number;
  isQuantityEditable?: boolean;
  visibleOnWebshop?: boolean;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of packages with filters
 */
export async function getPackages(filters: PackageFilters = {}): Promise<PackageListResponse> {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
  if (filters.search) params.append('search', filters.search);
  if (filters.scope) params.append('scope', filters.scope);
  if (filters.pricingModel) params.append('pricingModel', filters.pricingModel);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/packages?${queryString}` : '/packages';

  return api.get<PackageListResponse>(endpoint);
}

/**
 * Get single package by ID
 */
export async function getPackageById(id: string): Promise<PackageResponse> {
  return api.get<PackageResponse>(`/packages/${id}`);
}

/**
 * Get active packages by type (no pagination — returns flat array)
 */
export async function getPackagesByType(type: PackageType): Promise<PackagesByTypeResponse> {
  return api.get<PackagesByTypeResponse>(`/packages/type/${type}`);
}

/**
 * Create new package
 */
export async function createPackage(data: CreatePackageDTO): Promise<PackageResponse> {
  return api.post<PackageResponse>('/packages', data);
}

/**
 * Update package
 */
export async function updatePackage(id: string, data: UpdatePackageDTO): Promise<PackageResponse> {
  return api.patch<PackageResponse>(`/packages/${id}`, data);
}

/**
 * Delete package
 */
export async function deletePackage(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/packages/${id}`);
}

/**
 * Add item to package — returns the updated package with all items
 */
export async function addPackageItem(
  packageId: string,
  data: AddPackageItemDTO
): Promise<{ data: Package }> {
  return api.post<{ data: Package }>(`/packages/${packageId}/items`, data);
}

/**
 * Remove item from package — returns the updated package with remaining items
 */
export async function removePackageItem(
  packageId: string,
  itemId: string
): Promise<{ data: Package }> {
  return api.delete<{ data: Package }>(`/packages/${packageId}/items/${itemId}`);
}

/**
 * Update an existing package item attribute (PATCH) — itemType/FK immutable.
 * Returns the updated package with all remaining items.
 */
export async function updatePackageItem(
  packageId: string,
  itemId: string,
  data: UpdatePackageItemDTO
): Promise<{ data: Package }> {
  return api.patch<{ data: Package }>(`/packages/${packageId}/items/${itemId}`, data);
}

/**
 * Get active RENTAL packages linked to an equipment category.
 * Used in rental wizard EquipmentStep to surface relevant packages.
 */
export async function getPackagesByEquipmentCategory(
  category: string
): Promise<PackagesByEquipmentCategoryResponse> {
  return api.get<PackagesByEquipmentCategoryResponse>(
    `/packages/by-equipment-category/${encodeURIComponent(category)}`
  );
}

/**
 * Get packages linked to a specific rental equipment (by equipmentId).
 * Unified lookup: merges CATEGORY-linked + EQUIPMENT-linked packages.
 * Used in rental wizard and equipment detail views.
 */
export async function getPackagesForEquipment(
  equipmentId: string
): Promise<PackagesByEquipmentCategoryResponse> {
  return api.get<PackagesByEquipmentCategoryResponse>(
    `/packages/for-equipment/${encodeURIComponent(equipmentId)}`
  );
}

/**
 * Get packages that have a specific product linked (reverse lookup).
 * Used in product detail views to show which packages include this product.
 */
export async function getLinkedPackagesByProduct(
  productId: string
): Promise<PackagesByEquipmentCategoryResponse> {
  return api.get<PackagesByEquipmentCategoryResponse>(
    `/products/${encodeURIComponent(productId)}/linked-packages`
  );
}

/**
 * STORY-1206 #14 stage-2 — copy-on-write fork eredménye
 * (BE: POST /packages/:id/fork, #412 — @kgc/package-selection PackageCopyResult).
 */
export interface PackageCopyResult {
  newPackageId: string;
  newPackageCode: string;
  newPackageName: string;
  masterPackageId: string;
  productId: string;
  copiedItemCount: number;
  auditEventId: string;
}

export interface ForkPackageDTO {
  productId: string;
  reason?: string;
}

/**
 * Fork master package for one product (copy-on-write) — variant jön létre,
 * a kiválasztott gépmodell linkje a variantra mutat át, a master intakt.
 */
export async function forkPackage(
  id: string,
  data: ForkPackageDTO
): Promise<{ data: PackageCopyResult }> {
  return api.post<{ data: PackageCopyResult }>(`/packages/${id}/fork`, data);
}

// ============================================
// POS PACKAGE EXPANSION (Q-T043 — SALES-csomag POS-eladás)
// ============================================

/** Egy készlet-csökkentendő tétel a csomag POS-eladásakor (settlement=INCLUDED/PRODUCT). */
export interface PosPackageStockItem {
  productId: string;
  productName: string;
  quantity: number;
}

/**
 * Egy SALES-csomag POS-kibontása (BE↔FE kontraktus — `package-sale.dto.ts` tükre).
 * A csomag a blokkon 1 sorként jelenik meg (`unitPrice` = Package.fixedPrice),
 * de a készlet tételenként csökken (`stockItems`, BE atomi write — Q-T044).
 */
export interface PosPackageExpansion {
  packageId: string;
  packageCode: string;
  packageName: string;
  /** A csomag ára — Package.fixedPrice (1 billing-sor a blokkon). */
  unitPrice: number;
  /** ÁFA-kulcs (HU: 0/5/18/27). */
  taxRate: number;
  /** Készlet-csökkentendő tételek (settlement=INCLUDED/PRODUCT). */
  stockItems: PosPackageStockItem[];
  /**
   * A komponensek eredeti ár-összege (DISCOUNTED: Σ INCLUDED unitPrice; FIXED: ha minden
   * komponens-ár ismert). A »Szett-megtakarítás« delta = originalSum − unitPrice (ha > 0).
   * null, ha nem számolható. BE-additív (originalSum-PR, #726 után) — optional, a mostani FE nem törik.
   */
  originalSum?: number | null;
}

/**
 * SALES-csomag POS-kibontása — 1 billing-sor + N készlet-tétel.
 * BE: GET /packages/:id/pos-expansion (PackagePosExpansionService).
 */
export async function getPosPackageExpansion(
  packageId: string
): Promise<{ data: PosPackageExpansion }> {
  return api.get<{ data: PosPackageExpansion }>(
    `/packages/${encodeURIComponent(packageId)}/pos-expansion`
  );
}
